import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import cx from 'classnames'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { InlineLocationPicker } from 'components/InlineLocationPicker/InlineLocationPicker'
import { SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { AlmanacFileData } from 'hooks/queries/almanacFiles/useAlmanacFile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCallback, useEffect, useState } from 'react'
import { AlmanacFile, AncestorProject, FileType, HandbookV2 } from 'types'
import Styles from './CopyModal.module.scss'
import { CopyActions, SuccessfulCopyCallBack } from './components/CopyActions/CopyActions'
import { CopyTitleInput } from './components/CopyTitleInput/CopyTitleInput'
import { copyName } from './copyName'
import { useBrand } from 'hooks/useBrand'
import { Modal } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useOnItemCopied } from './useOnItemCopied'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ActionType } from 'components/LocationPicker/LocationPickerModal'
import { Placement } from '@floating-ui/react-dom'
import { LocationPickerModalButton } from 'components/LocationPicker/LocationPickerModalButton'

export interface Item {
  id?: string
  title: string
  header_emoji: string | null
  linked_copies_count: number
}

export interface CopiedItem {
  slug?: string
  handbook?: Pick<HandbookV2, 'seo_string' | 'key'>
  id?: string
}

export interface Destination {
  selectedFolder: SelectedFolder | null
  newWorkspaceId: number
}

export const CopyModal = observer(
  ({
    folder,
    item,
    itemType = 'article',
    closeModal,
    onItemCopied,
    onSubmit,
    file,
  }: {
    item: Item
    itemType?: FileType
    closeModal?: () => void
    folder?: AncestorProject | null
    onItemCopied?: SuccessfulCopyCallBack
    redirectOnSuccess?: boolean
    onSubmit?: (payload: {
      linked: boolean
      title: string
      destination_folder_key?: string
      destination_workspace_id: number
      share_with_same_people: boolean
    }) => void
    file?: AlmanacFile
  }) => {
    const { isMobile } = useResponsiveDesign()
    const { title: itemTitle } = item
    const title = `Copy ${itemTitle}`
    const currentWorkspaceId = useCurrentWorkspaceId()
    const [titleTouched, setTitleTouched] = useState(false)
    const [copyTitle, setCopyTitle] = useState(copyName(itemTitle))
    const [copyEmoji, setCopyEmoji] = useState<string | null>(item.header_emoji)
    const [destination, setDestination] = useState<Destination>({
      selectedFolder:
        folder && folder.handbook
          ? {
              id: folder.id,
              title: folder.title,
              seo_string: folder.handbook?.seo_string,
              handbook_key: folder.handbook.key,
            }
          : null,
      newWorkspaceId: currentWorkspaceId,
    })
    const [shareWithSamePeople, setShareWithSamePeople] = useState(false)
    const inDifferentWorkspace = Boolean(
      destination?.newWorkspaceId && destination.newWorkspaceId !== currentWorkspaceId,
    )
    const { defaultFoldersLocation } = useBrand()
    const { onItemCopied: defaultOnItemCopied } = useOnItemCopied({
      title: destination.selectedFolder?.title || '',
      fileType: itemType,
      folderSlug: destination.selectedFolder?.seo_string,
      handbooksStore: useRootStore().handbooks,
    })

    useEffect(() => {
      if (inDifferentWorkspace) {
        setShareWithSamePeople(false)
      }
    }, [inDifferentWorkspace])

    useEffect(() => {
      if (!titleTouched) {
        setCopyTitle(copyName(itemTitle))
      }
    }, [itemTitle, copyTitle, titleTouched])

    const handleNameChange = useCallback((newName: string) => {
      setTitleTouched(true)
      setCopyTitle(newName)
    }, [])

    const handleNameTaken = () => {
      showToastMessage('Name is taken. Use a different name.', 'error')
    }

    useEffect(() => {
      const folderId = folder?.id
      if (!folderId) return

      const checkForEditAccess = async () => {
        const response = await sendToApi<AlmanacFileData>(`/almanac_files/${folderId}`)

        if (response && response.type === SUCCESS_RESPONSE_TYPE) {
          const canEdit = response.data.almanac_file.permissions.can_edit
          if (!canEdit) setDestination({ selectedFolder: null, newWorkspaceId: currentWorkspaceId })
        }
      }

      checkForEditAccess()
    }, [folder, currentWorkspaceId])

    const handleSubmit = () => {
      onSubmit?.({
        linked: false,
        title: copyTitle,
        destination_folder_key: destination.selectedFolder?.id,
        destination_workspace_id: destination.newWorkspaceId,
        share_with_same_people: shareWithSamePeople,
      })
    }

    const locationProps = {
      buttonLabel: destination.selectedFolder?.title || defaultFoldersLocation,
      dropdownTriggerProps: { placement: 'bottom-start' as Placement, offsetOptions: { mainAxis: -40, crossAxis: 0 } },
      locationPickerProps: {
        actionType: 'copy' as ActionType,
        initialFolder: folder
          ? {
              id: folder.id,
              title: folder.title,
              ancestor_keys: folder.ancestor_projects.map((p) => p.id),
              default_view: folder.default_view,
              database_mode: folder.database_mode,
            }
          : null,
        initialWorkspaceId: currentWorkspaceId,
        onSelectFolder: setDestination,
        isRootDisabled: itemType === 'article',
        disabledFolderIds: itemType === 'folder' && item.id ? [item.id] : [],
      },
    }

    const locationPicker = (
      <div className={Styles.headerBravo}>
        <p>Copy in</p>
        <InlineLocationPicker {...locationProps} />
      </div>
    )

    return (
      <Modal.Root size="sm" onPressOutside={closeModal}>
        <Modal.Top>{locationPicker}</Modal.Top>

        <Modal.Header
          title={title}
          onClose={closeModal}
          actions={
            isMobile && (
              <LocationPickerModalButton
                folderName={locationProps.buttonLabel}
                locationPickerProps={locationProps.locationPickerProps}
              />
            )
          }
        />

        <Modal.Content>
          <div className={Styles.bodyBravo}>
            <div className={Styles.titleBravo}>
              <CopyTitleInput
                item={item}
                itemType={itemType}
                copyName={copyTitle}
                copyEmoji={copyEmoji}
                onCopyEmojiChange={setCopyEmoji}
                onNameChange={handleNameChange}
                file={file}
              />
            </div>
          </div>
          <Checkbox
            onChange={setShareWithSamePeople}
            size="lg"
            isSelected={shareWithSamePeople}
            isDisabled={inDifferentWorkspace}
            className={cx(Styles.checkbox, Styles.checkboxContent)}
          >
            Share copy with the same people
          </Checkbox>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="right">
            <CopyActions
              item={item}
              itemType={itemType}
              copyTitle={copyTitle}
              emoji={copyEmoji}
              destination={destination}
              onNameTaken={handleNameTaken}
              shareWithSamePeople={shareWithSamePeople}
              onSuccessfulCopy={onItemCopied || defaultOnItemCopied}
              onSubmit={onSubmit ? handleSubmit : undefined}
            />
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )
  },
)

export default CopyModal
